export const FrontDesk_SOP = {
    frontDeskEtiquette: {
        title: "Welcome Desk Etiquette & First Impressions",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/frontDeskEtiquetteTitle.png",
        body: `
          <section>
            <h3>🌟 First Impressions, Lasting Trust</h3>
            <p>
              The front desk is more than a counter — it's the heart of Pawsible's hospitality. 
              <span class="highlight">Warm greetings, attentive listening, and polished professionalism</span> set the tone for everything that follows. 
              Our goal is to <span class="highlight-green">make every client feel known, cared for, and excited to be here</span>.
            </p>
          </section>
      
          <section>
            <h4>👋 1. Greeting Paw Parents & Guests</h4>
            <ul>
              <li>😊 Smile, stand if seated, and greet them by name whenever possible</li>
              <li>🐶 Greet the pet too! (“Hi Luna! Ready for your spa/daycare day?”)</li>
              <li>📋 Ask warmly how we can help today — don’t just wait for them to speak first</li>
            </ul>
            <p><span class="highlight-blue">Hospitality starts with how we make people feel — not just what we say.</span></p>
          </section>
      
          <section>
            <h4>📞 2. Tone, Posture & Professionalism</h4>
            <ul>
              <li>🗣️ Speak clearly, calmly, and kindly — never rushed or dismissive</li>
              <li>🧍‍♀️ Stand tall with relaxed shoulders when engaging; maintain gentle eye contact</li>
              <li>📱 If on the phone when a client arrives, make eye contact and gesture politely that you’ll be right with them</li>
            </ul>
            <p><span class="highlight-green">We’re not just managing check-ins — we’re representing the brand.</span></p>
          </section>
      
          <section>
            <h4>📋 3. Attention to Detail</h4>
            <ul>
              <li>🗂️ Double-check appointment types, service add-ons, and special requests at drop-off</li>
              <li>🖊️ Label belongings clearly and confirm any food/medication items</li>
              <li>📲 Ensure client app info is accurate if something seems off or incomplete</li>
            </ul>
            <p><span class="highlight-yellow">Small details = big trust. One missed item can cause stress or miscommunication.</span></p>
          </section>
      
          <section>
            <h4>🔄 4. When Things Get Busy</h4>
            <ul>
              <li>🤝 Acknowledge every arriving client — even if you’re helping someone else</li>
              <li>🧘 Stay grounded; rushing creates more confusion and discomfort</li>
              <li>🙋‍♀️ Ask for help or acknowledge clients with a quick “We’ll be right with you!”</li>
            </ul>
            <p><span class="highlight-blue">Grace under pressure builds professionalism and calm trust in our care.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">The front desk is our welcome mat, our concierge, and our vibe check.</span> 
              Every eye contact, word, and gesture speaks to our values. 
              <strong>Be the reason someone feels excited to come back — every single time.</strong>
            </p>
          </section>
        `
      },
      parentAppSupport: {
        title: "Paw Parent App Walkthrough & FAQ",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/parentAppSupportTitle.png",
        body: `
          <section>
            <h3>📱 Confident Clients, Smooth Systems</h3>
            <p>
              The Pawsible app is a powerful tool for bookings, updates, and real-time care transparency.
              <span class="highlight">Your ability to walk paw parents through it — with patience and clarity — empowers them and reduces front desk friction</span>.
            </p>
          </section>
      
          <section>
            <h4>🧭 1. App Overview Talking Points</h4>
            <ul>
              <li>📆 Booking daycare, grooming, and training directly in the app</li>
              <li>📋 Viewing upcoming services and appointment history</li>
              <li>📸 Receiving report cards, photos, and shuttle updates</li>
              <li>💳 Managing payments and uploading vaccination records</li>
            </ul>
            <p><span class="highlight-blue">Offer a quick verbal walk-through, and show them key tabs on your screen if needed.</span></p>
          </section>
      
          <section>
            <h4>🙋 2. Common Client Questions</h4>
            <ul>
              <li>❓ “Where can I check if my dog’s booked for daycare?”</li>
              <li>❓ “How do I cancel or reschedule?”</li>
              <li>❓ “Can I upload new vaccine records myself?”</li>
              <li>❓ “Will I get updates when they’re picked up or dropped off?”</li>
            </ul>
            <p><span class="highlight-green">Reassure clients that yes — the app sends real-time updates, and staff are always available on support tab.</span></p>
          </section>
      
          <section>
            <h4>📲 3. Best Practices for In-Person Support</h4>
            <ul>
              <li>👂 Listen first — let them describe where they’re stuck</li>
              <li>📱 Offer to help on their phone or demo on yours</li>
              <li>📝 If it's too busy, invite them to swing by during the pick up time or later</li>
              <li>📎 Provide the link to the Pawsible Support page </li>
            </ul>
            <p><span class="highlight-yellow">Your support makes tech feel human — turn confusion into confidence in just a few clicks.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Our app is an extension of our care.</span> 
              Helping clients navigate it well shows that we’re tech-savvy and heart-led. 
              <strong>Every little walkthrough creates smoother days and stronger loyalty.</strong>
            </p>
          </section>
        `
      },
      newClientIntakeFD: {
        title: "New Client Intake Overview",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/newClientIntakeFDTitle.png",
        body: `
          <section>
            <h3>👋 First Impressions, Lasting Relationships</h3>
            <p>
              Welcoming a new client sets the tone for their entire experience at Pawsible.
              <span class="highlight">A warm, informed, and streamlined intake ensures trust and confidence from the start</span>.
            </p>
          </section>
      
            <section>
                <h4>📋 1. Helping Clients Sign Up & Set Up Profile</h4>
                <ul>
                    <li>📲 Guide them to the Pawsible app on browser</li>
                    <li>📝 Walk them through account creation (Google Auth recommended)</li>
                    <li>🐶 Assist in setting up their pet’s profile and store location</li>
                    <li>📸 Help upload vaccination records or explain how to</li>
                    <li>📆 Show them where to request services, manage bookings, and message the team</li>
                </ul>
                <p><span class="highlight-green">Make setup smooth and friendly — we want every new client to feel confident using our tools from day one.</span></p>
            </section>
      
          <section>
            <h4>🧠 2. Intake Notes to Pass to Team</h4>
            <p><span class="highlight-yellow">Recommend a temperament test for daycare - could conduct it after the grooming</span></p>
            <ul>
              <li>📝 Temperament notes: shy, excitable, leash-reactive, food allergies, etc.</li>
              <li>🧴 Grooming notes: matting, prior style, skin sensitivities</li>
              <li>🎯 Training goals if enrolling in programs</li>
              <li>🛡️ Shuttle instructions or handling preferences (e.g. “Needs slow intro to crate”)</li>
            </ul>
            <p><span class="highlight-blue">Front desk is the info hub — pass on helpful context so the team can prep accordingly.</span></p>
          </section>
      
          <section>
            <h4>💬 3. What to Say to New Clients</h4>
            <ul>
              <li>😊 “Welcome to Pawsible — we’re so excited to meet [Dog’s Name]!”</li>
              <li>📲 “We’ll get you set up in our app — that’s where you’ll manage bookings, see updates, and message us anytime.”</li>
              <li>🛡️ “Safety and comfort come first here — we’ll take things slow if your pup needs time to warm up.”</li>
              <li>🎉 “Let us know if you have any questions — we’re here to help every step of the way!”</li>
            </ul>
            <p><span class="highlight-yellow">Every new client should walk away feeling excited, supported, and like they found “their place.”</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Client trust is built in the first 5 minutes.</span>  
              A smooth, kind intake process sets up every department for success — and starts the relationship on the right paw.
            </p>
          </section>
        `
      },
      checkInOutProcess: {
        title: "Check-In/Out Process",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/checkInOutProcessTitle.png",
        body: `
          <section>
            <h3>🛎️ Smooth Starts & Happy Goodbyes: Check-In/Out Process</h3>
            <p>
              At Pawsible, the front desk experience sets the tone for the entire visit. 
              <span class="highlight">Efficient, warm, and accurate check-in/out ensures every pet is welcomed and every paw parent feels confident</span>.
            </p>
          </section>
      
          <section>
            <h4>📋 1. Check-In Procedure (All Services)</h4>
            <ul>
              <li>👋 Greet each client warmly with their pet’s name</li>
              <li>📲 Confirm the service booked in the dashboard (daycare, grooming, training, etc.)</li>
              <li>📝 Double-check notes: feeding, meds, alerts, temperament, shuttle info</li>
              <li>📦 Label and store belongings (leash, bag, treats) in designated cubby</li>
              <li>🐾 Notify team via internal channel (e.g., “Luna has checked in for full groom + daycare”)</li>
            </ul>
            <p><span class="highlight-blue">Clarity and cheerful tone matter — this is where trust begins.</span></p>
          </section>
      
          <section>
            <h4>📤 2. Check-Out Procedure</h4>
            <ul>
              <li>🎉 Celebrate the pet’s visit (“Luna had a great day with her pals today!”)</li>
              <li>📦 Return all belongings clearly and cleanly packed</li>
              <li>💬 Share key highlights if available (e.g., grooming notes, enrichment success, favorite play buddy)</li>
              <li>📲 Process payment if applicable and confirm future bookings or reminders</li>
              <li>🙌 Thank them by name and welcome them back — never end a visit silently</li>
            </ul>
            <p><span class="highlight-green">Check-out is the last impression — make it personal and smooth.</span></p>
          </section>
      
          <section>
            <h4>🚨 3. Notes for Special Situations</h4>
            <ul>
              <li>⚠️ If a dog is showing signs of illness, isolate immediately and notify a manager</li>
              <li>📸 If there's an incident or injury, follow the incident reporting SOP and alert the trainer or groomer</li>
              <li>🕓 If a client is late or missed pickup window, check app notes and follow front desk delay protocol</li>
            </ul>
            <p><span class="highlight-red">Accuracy and communication are your superpowers — especially when things don't go as planned.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Check-in/out is more than a transaction — it’s a connection.</span> 
              Be clear, be warm, and take pride in being the face of trust at Pawsible.
            </p>
          </section>
        `
      },
      tourProtocol: {
        title: "Tour SOP",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/tourProtocolTitle.png",
        body: `
          <section>
            <h3>🐾 Come See the Magic: Giving Memorable Tours</h3>
            <p>
              A great tour isn’t just a walkthrough — it’s <span class="highlight-green">a relationship-building moment</span>. 
              Every tour is a chance to <span class="highlight">build trust, showcase our standards, and make paw parents feel confident choosing Pawsible</span>.
            </p>
          </section>
      
          <section>
            <h4>📋 1. Before the Tour Begins</h4>
            <ul>
              <li>🗂️ Check calendar for scheduled tours or walk-ins</li>
              <li>🧼 Ensure all tour areas are clean, odor-free, and staged professionally (daycare, grooming, cat room, retail)</li>
              <li>📸 Keep visible happy pets and active staff engaged (no stress or mess in sight)</li>
              <li>💬 Ask the guest what services they’re most curious about to tailor the experience</li>
            </ul>
            <p><span class="highlight-blue">Tours are mini first dates — make them feel seen, not sold to.</span></p>
          </section>
      
          <section>
            <h4>🚶 2. Tour Route & Talking Points</h4>
            <ul>
              <li>🛎️ Welcome Area: Warm intro, Pawsible’s story and our full-service approach</li>
              <li>🐕 Daycare: Turf play areas, enrichment activities, temperament-based playgroups</li>
              <li>🛁 Grooming: Clean setup, gentle handling philosophy, and styling consistency</li>
              <li>🐾 Training: Positive reinforcement approach, private + group options</li>
              <li>🛏️ Boarding (if relevant): Crate setup, walk/potty schedules, app communication</li>
              <li>🛍️ Retail: Mention curated items, membership perks, or client favorites</li>
            </ul>
            <p><span class="highlight-green">Hit emotional trust points: safety, cleanliness, personalization, and love for every pet.</span></p>
          </section>
      
          <section>
            <h4>💡 3. Tour Tips & Best Practices</h4>
            <ul>
              <li>🧘 Walk slowly and speak clearly — don’t rush or oversell</li>
              <li>📣 Highlight visual moments (“We use enrichment to prevent burnout — see this themed playgroup?”)</li>
              <li>👂 Invite questions naturally (“Have you toured other daycares before?” “What’s important to you?”)</li>
              <li>📲 Mention the app: live chat, report cards, scheduling ease</li>
            </ul>
            <p><span class="highlight-blue">You’re not giving a tour. You’re showing them what makes us worth trusting with their best friend.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Tours are your chance to turn curiosity into connection.</span> 
              Show with pride, speak with warmth, and let Pawsible speak for itself through your energy.
            </p>
          </section>
        `
      },
      phoneCallSOP: {
        title: "Phone Call SOP",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/phoneCallSOPTitle.png",
        body: `
          <section>
            <h3>📞 Calm, Clear, Connected: Pawsible Phone SOP</h3>
            <p>
              Every phone call is a first impression or a relationship builder. At Pawsible, <span class="highlight">we aim to be friendly, informed, and solution-focused</span> — whether it's a curious new parent or a longtime client.
            </p>
          </section>
      
          <section>
            <h4>📋 1. Phone Etiquette Essentials</h4>
            <ul>
              <li>👋 Answer by the second ring with a warm tone: “Hi! Thank you for calling Pawsible — this is [Name], how can I help you today?”</li>
              <li>🧘 Stay calm and friendly — your voice sets the energy of the interaction</li>
              <li>📝 Take brief notes during the call to ensure no details are missed</li>
              <li>📲 Ask for name and pet name early — personalize the interaction</li>
            </ul>
            <p><span class="highlight-blue">Every call should feel personal and professional — never rushed or robotic.</span></p>
          </section>
      
          <section>
            <h4>🧭 2. Call Types & How to Handle</h4>
            <ul>
              <li>🆕 <strong>New client inquiries:</strong> Offer brief service overview, encourage a tour, and explain app sign-up</li>
              <li>🛎️ <strong>Booking requests:</strong> Use the system to check availability and confirm or log the request</li>
              <li>🛁 <strong>Service follow-ups:</strong> Review dog’s notes or trainer/groomer feedback before responding</li>
              <li>❓ <strong>General questions:</strong> Direct to appropriate FAQ or team member when needed</li>
              <li>⚠️ <strong>Concerns or complaints:</strong> Stay calm, validate the emotion, and take clear notes for management review</li>
            </ul>
            <p><span class="highlight-green">Use active listening: Don’t just answer questions — guide clients with empathy and clarity.</span></p>
          </section>
      
          <section>
            <h4>✍️ 3. Logging & Follow-Up</h4>
            <ul>
              <li>🗂️ Log new client inquiries and tour requests into the front desk system</li>
              <li>📲 Confirm client profile is updated after booking or app support call</li>
              <li>📩 For unresolved issues or complaints, notify manager or appropriate team via Slack/internal message</li>
              <li>🔁 Follow up promptly if client was promised a call/email back</li>
            </ul>
            <p><span class="highlight-blue">A clear record of each call keeps team communication smooth and clients feeling cared for.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Phone calls are small moments with big impact.</span>  
              A warm voice, a knowledgeable answer, and a personal touch turn simple calls into lasting trust.
            </p>
          </section>
        `
      },
      bookingCalendar: {
        title: "Client Booking Management & Calendar Updates",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/bookingCalendarTitle.png",
        body: `
          <section>
            <h3>🗓️ Organized, Accurate, On-Time: Booking & Calendar Mastery</h3>
            <p>
              At Pawsible, managing the booking calendar is more than scheduling — it’s about <span class="highlight">ensuring smooth operations, preventing double-bookings, and creating peace of mind for both clients and staff</span>.
            </p>
          </section>
      
          <section>
            <h4>📋 1. Client Booking SOP</h4>
            <ul>
              <li>📲 All bookings must go through the Pawsible app or internal system — no handwritten notes or side texts</li>
              <li>🗂️ Always confirm:
                <ul>
                  <li>✅ Service type (daycare, grooming, training, boarding, etc.)</li>
                  <li>✅ Drop-off and pick-up times</li>
                  <li>✅ Add-ons (nail trim, shuttle, training walk, etc.)</li>
                  <li>✅ Special notes or handling alerts (e.g., "anxious with dryers")</li>
                </ul>
              </li>
              <li>📆 Repeat or recurring requests must be logged clearly in both the system and client notes</li>
            </ul>
            <p><span class="highlight-blue">The clearer the booking, the smoother the service — no guesswork, no confusion.</span></p>
          </section>
      
          <section>
            <h4>🧭 2. Calendar Management Best Practices</h4>
            <ul>
              <li>🕘 Check calendar daily for overbooked or inconsistent service combinations</li>
              <li>🔁 Regularly cross-check app bookings with front desk schedule view</li>
              <li>⚠️ Immediately flag any double-bookings or capacity concerns to manager</li>
            </ul>
            <p><span class="highlight-green">Preventing scheduling bottlenecks starts with proactive eyes on the calendar — every shift.</span></p>
          </section>
      
          <section>
            <h4>📝 3. Editing & Canceling Bookings</h4>
            <ul>
              <li>📞 If a client calls to cancel/reschedule, confirm their identity and update in system immediately</li>
              <li>🔁 Add a short note to client profile (e.g., “Canceled due to travel – will rebook for next month”)</li>
              <li>❗ Ask for follow up booking if necessary</li>
              <li>🧾 For paid bookings, confirm if a refund, credit, or reschedule is required</li>
            </ul>
            <p><span class="highlight-yellow">Editing a booking isn’t just logistics — it’s client care. Keep it accurate and clear.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Booking management is a front desk superpower.</span> 
              When done with clarity, empathy, and attention to detail, it becomes a seamless experience that reflects Pawsible’s reliability and care.
            </p>
          </section>
        `
      },
      upsellReminders: {
        title: "Service Add-On Reminders & Upsell Flow",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/upsellingWithConfidenceTitle.png",
        body: `
          <section>
            <h3>💡 Upselling With Confidence & Care</h3>
            <p>
              At Pawsible, upselling isn’t pushy — it’s <span class="highlight-green">professional awareness of what the pet might need</span>. 
              Whether it’s a nail trim, enrichment walk, or blueberry facial, <span class="highlight">add-on services enhance comfort, safety, and client satisfaction</span>.
            </p>
          </section>
      
          <section>
            <h4>📋 1. When to Offer Service Add-Ons</h4>
            <ul>
              <li>🛁 At check-in: Offer grooming extras if the pet hasn’t had a service in a while</li>
              <li>🦴 During daycare or boarding: Suggest a training walk, enrichment session, or extra play add-on if energy is high or behavior benefits from it</li>
              <li>🚐 Shuttle drop-off/pick-up: Recommend touch-up services (nails, ear clean) if it's been over 4 weeks</li>
              <li>📆 Pre-holiday: Promote spa bundles or seasonal specials during peak periods</li>
            </ul>
            <p><span class="highlight-blue">Focus on what the dog would benefit from — not just what's on promo.</span></p>
          </section>
      
          <section>
            <h4>💬 2. Upsell Phrasing That Builds Trust</h4>
            <ul>
              <li>🌟 “It’s been a while since Luna’s last nail trim — want us to tidy them up during daycare today?”</li>
              <li>🧠 “Benny had lots of energy this morning — we could add a training walk to help him settle down this afternoon.”</li>
              <li>🧼 “Teddy’s coat is a little dry from the season change. Want to try our coconut conditioning rinse after his bath?”</li>
            </ul>
            <p><span class="highlight-green">Speak from care, not commission — clients know when you’re genuinely looking out for their dog.</span></p>
          </section>
      
          <section>
            <h4>🧭 3. Logging and Confirming Add-Ons</h4>
            <ul>
              <li>📲 Always update confirmed add-ons in the Pawsible app or internal booking system</li>
              <li>🗂️ Add notes to the client record if it’s a first-time or recurring request</li>
              <li>💳 Confirm pricing and billing before service is performed</li>
              <li>🔁 Notify the groomer, trainer, or daycare team if the add-on affects their workflow</li>
            </ul>
            <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/upsellingWithConfidenceRealLife.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            <p><span class="highlight-blue">Good upselling is seamless — the client says yes, and the whole team is ready.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Upselling at Pawsible is value-driven.</span> 
              When framed as a tailored recommendation, it deepens client trust and elevates every pet’s visit — from routine to remarkable.
            </p>
          </section>
        `
      },
      departmentCoordination: {
        title: "Multi-Service Coordination Across Departments",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/departmentCoordinationTitle.png",
        body: `
          <section>
            <h3>🔗 Connected Care: Coordinating Multi-Service Visits</h3>
            <p>
              At Pawsible, many pets enjoy multiple services in one visit — daycare, grooming, training, or shuttle. 
              <span class="highlight">Smooth coordination between departments</span> ensures <span class="highlight-green">a seamless, safe, and efficient experience for every dog and every team</span>.
            </p>
          </section>
      
          <section>
            <h4>📋 1. Recognizing Multi-Service Clients</h4>
            <ul>
              <li>📲 Always review the pet’s service tags in the Pawsible dashboard</li>
              <li>🗂️ Confirm all booked services at check-in and ensure all departments are aware</li>
              <li>📝 Add visible notes to the task boards if there are special priorities, like “Training before Grooming”</li>
            </ul>
            <p><span class="highlight-blue">Clear notes reduce confusion — and make every service feel intentional.</span></p>
          </section>
      
          <section>
            <h4>🔄 2. Cross-Team Timing Coordination</h4>
            <ul>
              <li>🕒 Confirm internal timing with other leads (e.g., “Groom at 1PM, finish by 2:30 for shuttle”)</li>
              <li>🧼 Ensure dogs are not sent into another service dirty, overstimulated, or late</li>
              <li>🔄 Use real-time updates in Slack or internal comms to notify next department when the dog is ready</li>
            </ul>
            <p><span class="highlight-green">Respect each team’s prep time — pass the dog in their best state.</span></p>
          </section>
      
          <section>
            <h4>✅ 3. Departure Prep for Multi-Service Clients</h4>
            <ul>
              <li>🧳 Confirm belongings are all returned (leash, carrier, go-home report, retail items, etc.)</li>
              <li>📸 Capture a go-home photo if time allows — especially after a fresh groom or successful training</li>
              <li>📩 Summarize services in the app notes or checkout message (“Daisy had a blast in daycare, crushed her training walk, and smells great post-groom!”)</li>
            </ul>
            <p><span class="highlight-blue">Make the final impression feel complete — like everything was handled with care.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Multi-service days are a full team performance.</span>  
              When we’re in sync across departments, pet parents feel it — and trust us even more with their dog's care.
            </p>
          </section>
        `
      },
      policyEnforcement: {
        title: "Late Pickup & Cancellation Policy Enforcement",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/policyEnforcementTitle.png",
        body: `
          <section>
            <h3>⏰ Boundaries Build Trust: Policy Enforcement with Compassion</h3>
            <p>
              At Pawsible, <span class="highlight">consistency in enforcing our time and cancellation policies</span> helps protect staff time, maintain operations, and <span class="highlight-green">reinforce mutual respect between the team and our clients</span>.
            </p>
          </section>
      
          <section>
            <h4>📋 1. Overview of Key Policies</h4>
            <ul>
              <li>⏱️ <strong>Daycare Late Pickup:</strong> Grace period is 15 minutes. After that, late fees apply per posted policy.</li>
              <li>📅 <strong>Same-Day Cancellations:</strong> Subject to full service charge unless due to emergency (with manager discretion).</li>
              <li>🕑 <strong>No-Show Appointments:</strong> Full charge applied; future bookings may require prepayment or deposit.</li>
            </ul>
            <p><span class="highlight-blue">Reminding clients clearly and early prevents conflict later.</span></p>
          </section>
      
          <section>
            <h4>💬 2. Scripts for Enforcement Conversations</h4>
            <ul>
              <li>😌 <strong>If late at pickup:</strong> 
                <br>
                “We totally understand that traffic can happen! Just a heads up — after the 15-minute grace period, our late fee policy does apply. We appreciate your understanding as it helps our team stay on schedule.”</li>
              <li>📞 <strong>If canceling same day:</strong> 
                <br>
                “I wanted to remind you that we do have a same-day cancellation policy in place to ensure fairness to all clients and staff. Today’s appointment will be charged per policy.”</li>
              <li>📲 <strong>If no-show:</strong> 
                <br>
                “We missed [Dog’s Name] today! Just a reminder that missed appointments are subject to our no-show policy. Let us know if you'd like to rebook — we may need to hold future bookings with a deposit.”</li>
            </ul>
            <p><span class="highlight-green">Clarity + kindness = firm boundaries with lasting trust.</span></p>
          </section>
      
          <section>
            <h4>📲 3. Logging & Reporting</h4>
            <ul>
              <li>📝 Note all late pickups, cancellations, or no-shows in the client’s dashboard</li>
              <li>🚩 Flag repeated infractions to management for follow-up and pattern tracking</li>
              <li>📬 Front desk may send a polite reminder email with relevant policy excerpt if needed</li>
            </ul>
            <p><span class="highlight-blue">Document everything — consistency keeps things fair for everyone.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Policies protect everyone’s time and energy.</span> 
              Enforce them with warmth, clarity, and confidence — because professionalism and kindness go hand in paw.
            </p>
          </section>
        `
      },
      paymentRefundProtocol: {
        title: "Payment Collection & Refund Protocol",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/paymentRefundProtocolTitle.png",
        body: `
          <section>
            <h3>💳 Clarity at Checkout: Payments, Refunds & Transparency</h3>
            <p>
              At Pawsible, <span class="highlight">payment handling is part of the client experience</span>. 
              Whether collecting, correcting, or refunding, we prioritize <span class="highlight-green">clarity, documentation, and empathy</span> — while protecting our team's time and the business’s financial health.
            </p>
          </section>
      
          <section>
            <h4>💰 1. Payment Collection</h4>
            <ul>
              <li>📲 Encourage all clients to pay via the Pawsible app (preferred method)</li>
              <li>💳 In-person payments (card or cash) should be logged in the Salesmate system immediately</li>
              <li>🧾 App automatically issues a digital receipt</li>
              <li>🧠 If unsure about a charge, <span class="highlight-blue">pause and confirm with management before collecting</span></li>
            </ul>
            <p><span class="highlight-green">Transparency builds trust — walk clients through charges if they ask.</span></p>
          </section>
      
          <section>
            <h4>💸 2. Refunds & Adjustments</h4>
            <ul>
              <li>📋 Refunds must be approved by a manager — no exceptions</li>
              <li>🔁 If service was unsatisfactory, offer to reschedule or apply credit first before refunding</li>
              <li>🛑 Do not promise refunds without manager approval — instead say: 
                <br><em>“Let me speak with our manager and we’ll follow up with the best solution for you.”</em></li>
              <li>📝 Always document the reason for refund/adjustment in the client's record</li>
            </ul>
            <p><span class="highlight-yellow">Refunds are rare, but when done right, they deepen client loyalty.</span></p>
          </section>
      
          <section>
            <h4>📌 3. Special Notes</h4>
            <ul>
              <li>🎁 Gift cards, memberships, and packages are <strong>non-refundable unless approved</strong> under special cases</li>
              <li>⏱️ Late pickups, no-shows, or cancellations still require charge collection per policy</li>
              <li>🧾 Always cross-check that multi-service invoices (grooming + training + retail) are fully itemized and correct</li>
            </ul>
            <p><span class="highlight-blue">Accuracy today prevents disputes tomorrow — triple-check when needed.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Money matters — so handle it with precision and professionalism.</span> 
              Be confident, be calm, and keep records. Our front desk is the final stop in a great client journey.
            </p>
          </section>
        `
      },
      writtenCommunicationFD: {
        title: "Email, Text & Chat Communication Standards",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/writtenCommunicationFDTitle.png",
        body: `
          <section>
            <h3>✉️ Professional, Personable, Pawsible: Writing Standards for Client Messages</h3>
            <p>
              Written communication reflects our brand voice. Whether via email, text, or in-app chat, we <span class="highlight">represent trust, warmth, and competence</span> — always keeping things clear, helpful, and kind.
            </p>
          </section>
      
          <section>
            <h4>💬 1. Tone & Voice Guidelines</h4>
            <ul>
              <li>😊 Friendly, clear, and professional — no slang or emojis unless context-appropriate</li>
              <li>🔤 Use full sentences and proper punctuation (even in texts)</li>
              <li>🎯 Stick to facts — avoid overpromising or giving personal opinions on care/services</li>
              <li>📣 Be consistent with Pawsible’s warm, emotionally intelligent brand tone</li>
            </ul>
            <p><span class="highlight-blue">Write like you speak on your best day — calm, helpful, and upbeat.</span></p>
          </section>
      
          <section>
            <h4>📲 2. Platform-Specific Best Practices</h4>
            <ul>
              <li><strong>Email:</strong> Use formal greetings (“Hi [Name],”), break content into short paragraphs, and include a clear closing (e.g., “Best, [Your Name] at Pawsible”)</li>
              <li><strong>Text:</strong> Short, direct, and clear (“Hi Mia! Luna is ready for pickup anytime after 3:30PM today!”)</li>
              <li><strong>Chat:</strong> Keep tone professional yet friendly — be quick and responsive but never curt</li>
            </ul>
            <p><span class="highlight-green">Match the medium — emails = polished, texts = concise, chats = clear and helpful.</span></p>
          </section>
      
          <section>
            <h4>🛑 3. What Not to Do</h4>
            <ul>
              <li>🚫 Never discuss internal issues, policies disputes, or staff drama via client message</li>
              <li>🚫 Don’t send photos or updates without appropriate context or permission</li>
              <li>🚫 Avoid “Hi” with no context — every message should contain value or direction</li>
              <li>🚫 No abbreviations like “pls” or “thx” in professional replies</li>
            </ul>
            <p><span class="highlight-red">Anything written can be screenshotted — write with professionalism always.</span></p>
          </section>
      
          <section>
            <h4>📋 4. Quick Response Templates</h4>
            <ul>
              <li><strong>Late Arrival Text:</strong> “Hi [Name], just a heads up that [Pet] hasn’t been picked up yet. We close at [time] — please let us know if you're on your way!”</li>
              <li><strong>Booking Confirmation:</strong> “Hi [Name]! We’ve scheduled [Pet] for [Service] on [Date] at [Time]. Let us know if anything changes!”</li>
              <li><strong>Policy Reminder:</strong> “Hi [Name], just a quick reminder of our 24-hour cancellation policy. Let us know if you need to make any changes!”</li>
            </ul>
            <p><span class="highlight-blue">Quick responses with clarity help keep communication efficient and respectful.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Every message is a brand moment.</span> 
              When we communicate with care, we reinforce professionalism, build trust, and ensure pet parents feel valued at every step.
            </p>
          </section>
        `
      },
      incidentEscalationFrontDesk: {
        title: "Incident Intake & Escalation to Manager",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/incidentEscalationFrontDeskTitle.png",
        body: `
          <section>
            <h3>🚨 Stay Calm, Document Clearly, Escalate Smartly</h3>
            <p>
              At Pawsible, every staff member plays a role in maintaining safety and transparency. The front desk is often the first to hear about concerns — <span class="highlight">your calm, professional response sets the tone</span> for resolution and care.
            </p>
          </section>
      
          <section>
            <h4>📋 1. What Qualifies as an Incident to Escalate</h4>
            <ul>
              <li>⚠️ Client expresses concern over pet injury, illness, or care mistake</li>
              <li>🐕 Physical injury (bite, limp, visible wound, excessive vomiting, diarrhea)</li>
              <li>😡 Escalated client frustration or policy dispute</li>
              <li>🛑 Staff safety concerns or unusual animal behavior reported by team</li>
              <li>💻 Technical issues affecting bookings, payments, or app functionality</li>
            </ul>
            <p><span class="highlight-blue">If it impacts a pet’s safety, a client’s trust, or the team’s wellbeing — it should be flagged.</span></p>
          </section>
      
          <section>
            <h4>📝 2. Immediate Front Desk Responsibilities</h4>
            <ul>
              <li>🧘 Stay calm and listen without interrupting — don’t rush resolution or make assumptions</li>
              <li>🖊️ Take notes with clear timestamps and direct quotes if possible</li>
              <li>📍 Ask for specifics: “When did you first notice this?” or “What did the groomer/trainer say?”</li>
              <li>📸 If applicable, take photos of visible injuries or item damage (with permission)</li>
            </ul>
            <p><span class="highlight-green">You're not solving the whole issue — you're gathering details to ensure accurate handoff.</span></p>
          </section>
      
          <section>
            <h4>📲 3. Escalation Protocol</h4>
            <ul>
              <li>📱 Notify manager on duty or appropriate lead immediately via internal channel (Slack, chat, or call)</li>
              <li>📂 Include name of client, pet, nature of incident, and any relevant attachments or screenshots</li>
              <li>🧾 Record the incident in the log if applicable (Training, Grooming, Boarding, etc.)</li>
              <li>📆 If the manager is unavailable, schedule a time for them to follow up with the client directly</li>
            </ul>
            <p><span class="highlight-red">Timely communication keeps things from escalating further. Be proactive, not reactive.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">The front desk is the communication bridge.</span> 
              The goal is not blame — it's clarity, calmness, and professionalism. Good documentation and respectful escalation protect everyone involved.
            </p>
          </section>
        `
      },
      retailInventory: {
        title: "Retail & Inventory",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/retailInventoryTitle.png",
        body: `
          <section>
            <h3>🛒 Merch with Meaning: Managing Pawsible Retail</h3>
            <p>
              At Pawsible, our retail section is more than just add-ons — it’s an <span class="highlight-green">extension of our brand, trust, and care philosophy</span>. 
              From harnesses to treats, everything we offer should reflect <span class="highlight">quality, relevance, and pet-safe curation</span>.
            </p>
          </section>
      
          <section>
            <h4>📦 1. Daily Retail Duties</h4>
            <ul>
              <li>🧼 Wipe down retail surfaces and straighten product displays at open and close</li>
              <li>🧾 Check that price tags are visible and up to date</li>
              <li>🧃 Refill small treat jars or sample items</li>
              <li>📦 Place low-stock markers in staff chat for reorder requests</li>
            </ul>
            <p><span class="highlight-blue">Clean, stocked, and well-presented = silent sales support.</span></p>
          </section>
      
          <section>
            <h4>📋 2. Inventory Management Procedures</h4>
            <ul>
              <li>🗂️ Inventory counts should be updated in the front desk dashboard weekly</li>
              <li>📉 Log expired/damaged products for removal (include photo + reason)</li>
              <li>🛒 Confirm incoming deliveries against order sheet and organize immediately</li>
              <li>📦 Label storage bins clearly (e.g., "Dachshund Harness – XS")</li>
              <li>🧾 Notify manager if backstock is running low on key items (e.g., pee pads, toothpaste, enrichment toys)</li>
            </ul>
            <p><span class="highlight-green">Well-managed inventory prevents waste, loss, and client frustration.</span></p>
          </section>
      
          <section>
            <h4>💬 3. Upsell With Integrity</h4>
            <ul>
              <li>🐾 Offer items that match the pet’s size, needs, and services (e.g., shampoo post-groom, calming chews after training)</li>
              <li>🧠 Know the “why” — be ready to explain product purpose and benefits</li>
              <li>💚 Never pressure or oversell — our goal is helpful, not pushy</li>
              <li>📲 Mention product use in daycare/grooming reports if appropriate</li>
            </ul>
            <p><span class="highlight-yellow">When a parent trusts your care, your product suggestion carries weight. Use it responsibly.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Retail is part of the experience, not an afterthought.</span> 
              Keep it clean. Keep it stocked. Offer only what we’d give our own pets — and own the professionalism of that promise.
            </p>
          </section>
        `
      },
      lostFound: {
        title: "Lost & Found Protocol",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/lostFoundTitle.png",
        body: `
          <section>
            <h3>🎒 Lost Items, Lasting Trust</h3>
            <p>
              At Pawsible, managing lost & found isn’t just about returning items — it’s about <span class="highlight">protecting trust, showing professionalism, and preventing repeat mistakes</span>.
            </p>
          </section>
      
          <section>
            <h4>🧾 1. Common Lost Items</h4>
            <ul>
              <li>🦴 Leashes, collars, harnesses</li>
              <li>🧥 Jackets or pet clothing</li>
              <li>🧸 Toys, chews, enrichment items</li>
              <li>🛍️ Client totes or gear bags</li>
              <li>📦 Personal bedding or blankets</li>
            </ul>
            <p><span class="highlight-blue">Labeling and logging prevent confusion and protect the client's belongings.</span></p>
          </section>
      
          <section>
            <h4>📋 2. Logging & Labeling Found Items</h4>
            <ul>
              <li>🔖 Tag item with dog’s name + date found</li>
              <li>🗂️ Log item in the Lost & Found sheet (location, description, client name if known)</li>
              <li>📸 Optional: Take photo if item is high-value or unique</li>
              <li>🧼 Place in designated lost & found bin in front desk storage</li>
            </ul>
            <p><span class="highlight-green">Every item matters to someone — log it like it’s yours.</span></p>
          </section>
      
          <section>
            <h4>📩 3. Notifying Clients</h4>
            <ul>
              <li>📲 If client is still onsite, return the item at checkout</li>
              <li>📤 If client has left, message via text or in-app chat with short note (“We found Max’s harness and have it at the front desk!”)</li>
              <li>📌 Offer to hold item for 7 days (unless otherwise requested)</li>
            </ul>
            <p><span class="highlight-yellow">Timely follow-up shows attention to detail — and builds lasting loyalty.</span></p>
          </section>
      
          <section>
            <h4>🧹 4. Unclaimed Item Disposal</h4>
            <ul>
              <li>🗓️ If unclaimed after 14 days, move to donation bin or discard (check item condition first)</li>
              <li>📋 Log disposal in Lost & Found sheet with date removed</li>
              <li>🛑 Do not donate anything with clear client personalization (names, embroidery, etc.)</li>
            </ul>
            <p><span class="highlight-red">No items should be reused, gifted, or tossed without proper protocol — respect is the default.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Little things leave big impressions.</span> 
              The way we handle lost items speaks volumes about our culture of care.
            </p>
          </section>
        `
      }      
}